export type ClientPlatform = "web" | "ios" | "steam_desktop";

declare global {
  interface Window {
    Capacitor?: {
      getPlatform?: () => string;
      isNativePlatform?: () => boolean;
    };
    clawwarsDesktop?: {
      platform?: ClientPlatform;
      apiBaseUrl?: string;
      wsUrl?: string;
    };
  }
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

function hasNativeIOSHandlers(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const handlers = window.webkit?.messageHandlers;
  return Boolean(handlers?.appleSignIn || handlers?.storekit);
}

function isIOSUserAgent(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }

  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function normalizeApiPath(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return normalized.startsWith("/api") ? normalized : `/api${normalized}`;
}

function getConfiguredPlatform(): ClientPlatform {
  if (hasNativeIOSHandlers()) {
    return "ios";
  }

  const capacitorPlatform = window.Capacitor?.getPlatform?.();
  if (capacitorPlatform === "ios") {
    return "ios";
  }

  if (window.Capacitor?.isNativePlatform?.() && capacitorPlatform !== "web") {
    return "ios";
  }

  const desktopPlatform = window.clawwarsDesktop?.platform;
  if (desktopPlatform === "steam_desktop" || desktopPlatform === "ios" || desktopPlatform === "web") {
    return desktopPlatform;
  }

  const envPlatform = import.meta.env.VITE_PLATFORM;
  if (envPlatform === "steam_desktop" || envPlatform === "ios") {
    return envPlatform;
  }

  if (
    isIOSUserAgent() &&
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ) {
    return "ios";
  }

  return "web";
}

export function getClientPlatform(): ClientPlatform {
  if (typeof window === "undefined") {
    return "web";
  }

  return getConfiguredPlatform();
}

export function isSteamDesktop(): boolean {
  return getClientPlatform() === "steam_desktop";
}

export function isIOSBuild(): boolean {
  return getClientPlatform() === "ios";
}

export function getApiBaseUrl(): string {
  if (typeof window === "undefined") {
    return trimTrailingSlash(import.meta.env.VITE_API_BASE_URL || "");
  }

  const desktopBase = window.clawwarsDesktop?.apiBaseUrl;
  if (desktopBase) {
    return trimTrailingSlash(desktopBase);
  }

  const envBase = import.meta.env.VITE_API_BASE_URL;
  if (envBase) {
    return trimTrailingSlash(envBase);
  }

  return trimTrailingSlash(window.location.origin);
}

export function getApiUrl(path: string): string {
  const normalizedPath = normalizeApiPath(path);
  const baseUrl = getApiBaseUrl();

  if (getClientPlatform() === "web" && !import.meta.env.VITE_API_BASE_URL) {
    return normalizedPath;
  }

  return `${baseUrl}${normalizedPath}`;
}

export function getPublicAppOrigin(): string {
  const apiBase = getApiBaseUrl();
  return apiBase || (typeof window !== "undefined" ? window.location.origin : "");
}

export function getWebSocketUrl(token?: string): string {
  const query = token ? `?token=${encodeURIComponent(token)}` : "";
  const desktopWsUrl = typeof window !== "undefined" ? window.clawwarsDesktop?.wsUrl : undefined;
  const configuredWsUrl = desktopWsUrl || import.meta.env.VITE_WS_URL;

  if (configuredWsUrl) {
    const normalized = trimTrailingSlash(configuredWsUrl);
    return normalized.includes("/ws") ? `${normalized}${query}` : `${normalized}/ws${query}`;
  }

  if (typeof window === "undefined") {
    return query ? `/ws${query}` : "/ws";
  }

  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ws${query}`;
}
